import React, { useEffect, useState } from 'react';
import { adminUpdateAppUser } from '../../../api/appUsersApi';
import './styles/UserDetails.css';

const UserDetails = ({ user, setUser }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    company: user?.company || '',
    notes: user?.notes || ''
  });

  useEffect(() => {
    // Keep form in sync when the user changes (unless actively editing)
    if (editing) return;
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      company: user?.company || '',
      notes: user?.notes || ''
    });
  }, [user?.id, editing]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const onToggleEdit = () => {
    setSuccess(false);
    setError(null);
    setEditing((v) => !v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const updated = await adminUpdateAppUser(user.id, formData);
      setUser((prev) => ({
        ...prev,
        ...formData,
        ...(updated || {}),
      }));
      
      setSuccess(true);
      setEditing(false);
    } catch (err) {
      setError('Error updating user details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-card user-section-card userDetailsCard" aria-busy={loading ? 'true' : 'false'}>
      {success ? (
        <div className="udAlert udAlertSuccess" role="status" aria-live="polite">
          User details updated successfully.
        </div>
      ) : null}

      {error ? (
        <div className="udAlert udAlertError" role="alert" aria-live="polite">
          {error}
        </div>
      ) : null}

      <div className="udHeader">
        <div>
          <h3 className="udTitle">User Details</h3>
          <p className="udSub">Update basic profile fields and internal notes.</p>
        </div>
        <div className="udBtnRow">
          <button type="button" className="udBtn" onClick={onToggleEdit} disabled={loading}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="udFormGrid">
          <div className="udField">
            <label htmlFor="udFirstName">First name</label>
            <input
              id="udFirstName"
              className="udInput"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={!editing}
              autoComplete="given-name"
            />
          </div>

          <div className="udField">
            <label htmlFor="udLastName">Last name</label>
            <input
              id="udLastName"
              className="udInput"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={!editing}
              autoComplete="family-name"
            />
          </div>

          <div className="udField">
            <label htmlFor="udPhone">Phone</label>
            <input
              id="udPhone"
              className="udInput"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!editing}
              autoComplete="tel"
            />
          </div>

          <div className="udField">
            <label htmlFor="udCompany">Company</label>
            <input
              id="udCompany"
              className="udInput"
              name="company"
              value={formData.company}
              onChange={handleChange}
              disabled={!editing}
            />
          </div>

          <div className="udField udSpan2">
            <label htmlFor="udNotes">Notes</label>
            <textarea
              id="udNotes"
              className="udTextarea"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              disabled={!editing}
              rows={5}
              placeholder="Internal notes…"
            />
          </div>
        </div>

        {editing ? (
          <div className="udFooter">
            <button type="submit" className="udBtn udBtnPrimary" disabled={loading}>
              {loading ? (
                <>
                  <span className="udSpinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default UserDetails; 